/**
 * Platform-agnostic cleanup for operator/CMS rich HTML before `dangerouslySetInnerHTML`
 * or native WebView render. Extend here rather than per-screen CSS when possible.
 */
export function compactRichHtml(raw: string): string {
  let s = raw
    // Embedded Word/CMS stylesheets fight the host layout (negative indents, fixed widths).
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, "")
    // Spacer paragraphs between real content (EN/ES pairs, CMS gaps).
    .replace(/<p[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*\s*<\/p>/gi, "")
    .replace(/<(p|div)>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/\1>/gi, "")
    // CMS bullets often use <p><br/></p> between items → huge vertical gaps on web.
    .replace(/<p>\s*(?:<br\s*\/?>\s*)+<\/p>/gi, "")
    // Spacer paragraphs between <li> rows (outside inner <p> cleanup).
    .replace(/<\/li>\s*<p>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*(?=<li)/gi, "</li>")
    // Fake bullets: consecutive <p> that are only a bullet glyph + text (collapse to a real ul later is risky; strip br-only p between).
    .replace(/<\/p>\s*<p>\s*(?:&nbsp;|\s|<br\s*\/?>)*<\/p>\s*<p>/gi, "</p><p>")
    // Multiple <br> between paragraphs (Word / CMS) → single break worth of gap.
    .replace(/<\/p>\s*(?:<br\s*\/?>\s*){2,}\s*<p/gi, "</p><p>")
    .replace(/(?:<br\s*\/?>\s*){3,}/gi, "<br/><br/>");

  let prevStrip = "";
  for (let g = 0; g < 12 && s !== prevStrip; g++) {
    prevStrip = s;
    s = s.replace(/(<\/p>)\s*(<p[^>]*>)\s*<\/p>/gi, "$1");
  }

  // Word / rich-text exports: deep <span class="font"><span class="size"><br/></span></span> stacks (no text).
  prevStrip = "";
  for (let g = 0; g < 28 && s !== prevStrip; g++) {
    prevStrip = s;
    s = s.replace(
      /<(?:span|font|b|i|strong|em|u)\b[^>]*>\s*(?:&nbsp;|\u00a0|\s|<br\s*\/?>)*<\/(?:span|font|b|i|strong|em|u)>/gi,
      "",
    );
  }

  prevStrip = "";
  for (let g = 0; g < 20 && s !== prevStrip; g++) {
    prevStrip = s;
    s = s.replace(/<div\b[^>]*>\s*(?:<br\s*\/?>\s*)*<\/div>/gi, "");
    s = s.replace(/<div\b[^>]*>\s*<\/div>/gi, "");
  }

  // Leftover breaks between block tags (after span collapse).
  s = s.replace(
    /(<\/(?:div|p|li|ul|ol|h[1-6])>)\s*(?:<br\s*\/?>\s*)+(?=<(?:div|p|li|ul|ol|h[1-6])\b)/gi,
    "$1",
  );
  // Remove background-color / background from inline styles (messy operator HTML).
  s = s.replace(
    /\sstyle\s*=\s*(["'])([^"']*)\1/gi,
    (_m, q: string, style: string) => {
      const next = style
        .replace(/background-color\s*:\s*[^;]+;?/gi, "")
        .replace(/background\s*:\s*[^;]+;?/gi, "")
        // CMS HTML often sets huge margins/line-height on lists and list items → triple spacing on web.
        .replace(/margin(?:-(?:top|bottom|left|right))?\s*:\s*[^;]+;?/gi, "")
        .replace(/padding(?:-(?:top|bottom|left|right))?\s*:\s*[^;]+;?/gi, "")
        .replace(/line-height\s*:\s*[^;]+;?/gi, "")
        // Word hanging-indent lists use negative text-indent once margins are stripped → left overflow on web.
        .replace(/text-indent\s*:\s*[^;]+;?/gi, "")
        .replace(/position\s*:\s*[^;]+;?/gi, "")
        .replace(/(?:left|right|top|bottom)\s*:\s*[^;]+;?/gi, "")
        .replace(/width\s*:\s*[^;]+;?/gi, "")
        .replace(/;\s*;/g, ";")
        .replace(/^\s*;\s*|\s*;\s*$/g, "")
        .trim();
      if (!next) return "";
      return ` style=${q}${next}${q}`;
    },
  );
  // Collapse double <br> after headings (common CMS pattern → huge gaps).
  s = s.replace(/(<\/h[1-6]>)\s*(?:<br\s*\/?>\s*){2,}/gi, "$1<br/>");

  // Glued anchors: browsers/RN glue text to <a> when there's no whitespace node.
  // e.g. CMS: <span>visit</span><a href="...">www...</a> → "visitwww" without a fix.
  s = s.replace(/(<\/(?:span|font|b|i|strong|em|u)\b>)(<a\b)/gi, "$1 $2");
  s = s.replace(/(<\/a>)(<a\b)/gi, "$1 $2");
  s = s.replace(/([a-zA-Z0-9)])(<a\b)/gi, "$1 $2");
  s = s.replace(/(<\/a>)([a-zA-Z0-9])/gi, "$1 $2");

  return s.trim();
}
